/**
 * PATH: packages/ui/src/kanban/preferences/KanbanSettingsPreferenceProvider.tsx
 * FILE_ID: UI-KANBAN-PREFERENCES-PROVIDER-001
 * PURPOSE: Hydrate Kanban durable preference scopes once per board runtime.
 * ROLE IN ARCHITECTURE: Board-level provider above cards, menus, settings, and drawer.
 * MAIN EXPORTS: KanbanSettingsPreferenceProvider.
 * NON-RESPONSIBILITIES: Does not render controls or alter preference semantics.
 * NOTES FOR FUTURE AI: Never move these hooks into card-level consumers.
 */

import { useMemo, type ReactNode } from 'react'

import { useDbViewPreference } from '@quotech/tenant-shared/preferences/useDbViewPreference'
import { resolveViewPreferenceIdentity } from '@quotech/tenant-shared/preferences/quotechViewPreferenceAdapter'

import {
  KanbanSettingsPreferenceContext,
  type KanbanPreferenceSlice,
  type KanbanSettingsPreferenceContextValue,
} from './KanbanSettingsPreferenceContext'

type CardFacePrefs =
  KanbanSettingsPreferenceContextValue['cardFace']['value']
type CardTabLayout =
  KanbanSettingsPreferenceContextValue['cardTabLayout']['value']
type DrawerSectionPrefs =
  KanbanSettingsPreferenceContextValue['drawerSections']['value']

export interface KanbanPreferenceDefinition<T extends object> {
  scopeKey: string
  schemaVersion: number
  defaults: T
  coerce: (raw: unknown) => T
}

export interface KanbanSettingsPreferenceDefinitions {
  cardFace: KanbanPreferenceDefinition<CardFacePrefs>
  cardTabLayout: KanbanPreferenceDefinition<CardTabLayout>
  drawerSections: KanbanPreferenceDefinition<DrawerSectionPrefs>
}

export interface KanbanSettingsPreferenceProviderProps {
  boardId: string
  boardKind?: string | null
  userId?: string | number | null
  definitions: KanbanSettingsPreferenceDefinitions
  children: ReactNode
}

function slice<T extends object>(
  preference: ReturnType<typeof useDbViewPreference<T>>,
): KanbanPreferenceSlice<T> {
  return {
    value: preference.value,
    setValue: preference.setValue,
    hydrated: preference.hydrated,
    syncState: preference.syncState,
    conflict: preference.conflict,
    error: preference.error,
  }
}

/**
 * Provide one shared hydrate/subscription per durable Kanban preference scope.
 *
 * Inputs: board identity, optional kind, authenticated user, and descendants.
 * Output: context consumed by cards, menus, settings, and drawer preference hooks.
 * Side effects: local cache subscriptions and three scope-level server hydrates.
 * Failure modes: sync/conflict errors remain in context while local drafts survive.
 */
export function KanbanSettingsPreferenceProvider({
  boardId,
  boardKind,
  userId,
  definitions,
  children,
}: KanbanSettingsPreferenceProviderProps) {
  const identity = useMemo(() => resolveViewPreferenceIdentity(userId), [userId])
  const hydrateFromServer =
    userId !== null && userId !== undefined && String(userId).trim() !== ''
  const cardFace = useDbViewPreference({
    identity,
    ...definitions.cardFace,
    hydrateFromServer,
  })
  const cardTabLayout = useDbViewPreference({
    identity,
    ...definitions.cardTabLayout,
    hydrateFromServer,
  })
  const drawerSections = useDbViewPreference({
    identity,
    ...definitions.drawerSections,
    hydrateFromServer,
  })
  const value = useMemo<KanbanSettingsPreferenceContextValue>(
    () => ({
      boardId,
      boardKind,
      cardFace: slice(cardFace),
      cardTabLayout: slice(cardTabLayout),
      drawerSections: slice(drawerSections),
    }),
    [boardId, boardKind, cardFace, cardTabLayout, drawerSections],
  )

  return (
    <KanbanSettingsPreferenceContext.Provider value={value}>
      {children}
    </KanbanSettingsPreferenceContext.Provider>
  )
}
